import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CheckCircle, Warning } from "@phosphor-icons/react";
import { ChoicePills, EmployeeCombo, DateField, TextAnswer } from "./QuestionInputs";

const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? null : Number(v));
export function isVisible(q, a) {
  const c = q.show_if;
  if (!c || !c.q) return true;
  const x = a[c.q];
  if (x == null || x === "") return false;
  if (["gte", "lte", "gt", "lt"].includes(c.op)) {
    const p = num(x), r = num(c.value);
    if (p == null || r == null) return false;
    return { gte: p >= r, lte: p <= r, gt: p > r, lt: p < r }[c.op];
  }
  return c.op === "neq" ? String(x) !== String(c.value) : String(x) === String(c.value);
}

function Progress({ total, done }) {
  return (
    <div className="flex gap-1 px-3 pt-2" data-testid="tsf-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i < done ? "bg-[#327de3]" : "bg-[#327de3]/15"}`} />
      ))}
    </div>
  );
}

function Answer({ q, value, set, pick, employees, jobs }) {
  if (q.kind === "employee") return <EmployeeCombo names={employees} value={value} onChange={pick} placeholder={q.placeholder} />;
  if (q.kind === "date") return <DateField value={value && value !== "invalid" ? value : ""} onChange={set} onPicked={pick} />;
  if (q.kind === "choice") return <ChoicePills options={q.options.map((o) => ({ value: o, label: o }))} value={value} onChange={pick} testId={`tsf-choice-${q.id}`} />;
  if (q.kind === "jobsite") return <ChoicePills options={jobs.map((j) => ({ value: j.id, label: j.label }))} value={value} onChange={pick} testId={`tsf-choice-${q.id}`} />;
  return <TextAnswer kind={q.kind} value={value} onChange={set} />;
}

const AUTO = ["employee", "choice", "jobsite"];

/** One-question-at-a-time timesheet form. Used by the public page and the Form Management preview. */
export default function FormRunner({ schema, employees = [], jobs = [], lockedEmployee, onSubmit, compact = false }) {
  const [answers, setAnswers] = useState(lockedEmployee ? { name: lockedEmployee } : {});
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState("next");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [dup, setDup] = useState(null);
  const bodyRef = useRef(null);
  const advancing = useRef(false);
  const visibleFor = (a) => schema.questions.filter((x) => !(lockedEmployee && x.id === "name") && isVisible(x, a));
  const qs = useMemo(() => visibleFor(answers), [schema, answers, lockedEmployee]); // eslint-disable-line react-hooks/exhaustive-deps
  const q = qs[Math.min(idx, qs.length - 1)];
  const answered = q && answers[q.id] != null && answers[q.id] !== "";
  // An unanswered auto-advance step (e.g. Locations) may hide the steps after it; only offer Submit once answered.
  const last = idx >= qs.length - 1 && (answered || !AUTO.includes(q?.kind));

  useEffect(() => { setErr(""); advancing.current = false; bodyRef.current?.scrollTo?.({ top: 0 }); }, [idx]);

  const set = (v) => { setErr(""); setAnswers((a) => ({ ...a, [q.id]: v })); };
  // Selecting a pill / name / calendar day saves the answer and slides to the next applicable question.
  // Hidden jobsite answers (after lowering the count) are kept locally but never submitted.
  const pick = (v) => {
    if (busy || advancing.current) return;
    setErr("");
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    if (idx < visibleFor(next).length - 1) {
      advancing.current = true;
      setTimeout(() => { setDir("next"); setIdx((i) => i + 1); }, 180);
    }
  };
  const validate = () => {
    const v = answers[q.id];
    if (q.kind === "date" && v === "invalid") return "Please enter a real date (MM/DD/YYYY)";
    if ((v == null || v === "") && q.required) return q.kind === "date" ? "Please enter the date you worked" : "Please select an answer";
    if (q.kind === "number" && v !== "" && v != null && num(v) == null) return "Please enter a number";
    return "";
  };
  const submit = async (confirmDuplicate = false) => {
    setBusy(true);
    setErr("");
    const sent = Object.fromEntries(schema.questions.filter((x) => isVisible(x, answers) || x.id === "name").map((x) => [x.id, answers[x.id]]).filter(([, v]) => v != null && v !== ""));
    try {
      const r = await onSubmit(sent, confirmDuplicate);
      if (r?.duplicate) setDup(r.msg);
      else { setDup(null); setDone(r || {}); }
    } catch (e) {
      setErr(e?.message || "Couldn't save your timesheet. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };
  const ok = () => {
    if (busy) return;
    const e = validate();
    if (e) return setErr(e);
    if (last) return submit(false);
    setDir("next");
    setIdx((i) => i + 1);
  };
  const back = () => { if (idx > 0 && !busy) { setDir("prev"); setIdx((i) => i - 1); setDup(null); } };
  const reset = () => { setAnswers(lockedEmployee ? { name: lockedEmployee } : {}); setIdx(0); setDone(null); setDir("next"); };

  useEffect(() => {
    const onKey = (e) => {
      if (done || e.target?.tagName === "INPUT") { if (e.key === "Enter" && !done) ok(); return; }
      if (e.key === "Enter") return ok();
      const choices = q?.kind === "choice" ? q.options : q?.kind === "jobsite" ? jobs.map((j) => j.id) : null;
      const i = e.key.length === 1 ? e.key.toUpperCase().charCodeAt(0) - 65 : -1;
      if (choices && i >= 0 && i < choices.length) pick(choices[i]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const h = compact ? "h-full" : "min-h-[100dvh]";
  if (done) {
    return (
      <div className={`${h} flex flex-col bg-[#F7F8FA] tsf-light`} data-testid="tsf-thank-you">
        <Progress total={qs.length} done={qs.length} />
        <div className="px-5 pt-4 text-[13px] font-bold tracking-[0.25em] text-[#1e293b]">FRIEDMAN GROUP</div>
        <div className="tsf-pop flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
          <CheckCircle size={56} weight="fill" className="text-[#2f78dc]" />
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0f172a]">{schema.thank_you?.title || "Thank you"}</h1>
          <p className="text-[#475569] max-w-sm" data-testid="tsf-thank-you-msg">{schema.thank_you?.message}</p>
          {done.employee && <p className="text-sm text-[#64748b]">{done.employee} · {done.work_date && new Date(`${done.work_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</p>}
          <button type="button" onClick={reset} className="mt-2 h-11 rounded-lg border border-[#cbd5e1] bg-[#ffffff] px-5 text-sm font-semibold text-[#334155] hover:bg-[#f8fafc]" data-testid="tsf-submit-another">Submit another timesheet</button>
        </div>
      </div>
    );
  }
  if (!q) return null;
  const pos = idx + 1;
  return (
    <div className={`${h} flex flex-col bg-[#F7F8FA] tsf-light`} data-testid="tsf-runner">
      <Progress total={qs.length} done={idx} />
      <div className="px-5 pt-4 flex items-center justify-between">
        <span className="text-[13px] font-bold tracking-[0.25em] text-[#1e293b]">FRIEDMAN GROUP</span>
        {lockedEmployee && <span className="text-xs text-[#64748b]" data-testid="tsf-locked-employee">{lockedEmployee}</span>}
      </div>
      <div ref={bodyRef} className="flex-1 overflow-y-auto">
        <div key={`${q.id}-${idx}`} className={`${dir === "next" ? "tsf-next" : "tsf-prev"} w-full max-w-xl mx-auto px-5 sm:px-6 pt-8 pb-32`} data-testid={`tsf-question-${q.id}`}>
          <div className="flex items-start gap-3 mb-1">
            <span className="mt-1 shrink-0 inline-flex h-6 min-w-[24px] px-1 items-center justify-center rounded-md bg-[#327de3] text-[#ffffff] text-xs font-bold">{pos}</span>
            <h1 className="text-[22px] sm:text-[26px] leading-snug font-bold text-[#0f172a]" data-testid="tsf-question-title">{q.title}{q.required && <span className="text-[#2f78dc]">*</span>}</h1>
          </div>
          {q.helper && <p className="ml-9 mb-6 whitespace-pre-line text-[14px] text-[#64748b]" data-testid="tsf-question-helper">{q.helper}</p>}
          <div className="ml-0 sm:ml-9"><Answer q={q} value={answers[q.id]} set={set} pick={pick} employees={employees} jobs={jobs} /></div>
        </div>
      </div>
      <div className={`${compact ? "absolute" : "fixed"} bottom-0 inset-x-0 border-t border-[#e2e8f0] bg-[#F7F8FA] backdrop-blur px-4 pt-3`} style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
        <div className="max-w-xl mx-auto space-y-2 [&:not(:empty)]:mb-3">
            {err && <div className="inline-flex items-center gap-2 rounded-md bg-[#fff1f2] px-3 py-2 text-sm text-[#be123c]" role="alert" data-testid="tsf-error"><Warning size={16} weight="bold" />{err}</div>}
            {dup && (
              <div className="rounded-lg border border-[#fde68a] bg-[#fffbeb] p-3 text-sm text-[#78350f]" data-testid="tsf-duplicate">
                <p>{dup}</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" disabled={busy} onClick={() => submit(true)} className="h-10 rounded-lg bg-[#0f172a] px-4 text-[#ffffff] font-semibold" data-testid="tsf-duplicate-confirm">Submit anyway</button>
                  <button type="button" onClick={() => setDup(null)} className="h-10 rounded-lg border border-[#fcd34d] px-4" data-testid="tsf-duplicate-cancel">Cancel</button>
                </div>
              </div>
            )}
        </div>
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <button type="button" onClick={back} disabled={idx === 0 || busy} aria-label="Back" className="h-12 w-12 shrink-0 rounded-lg bg-[#0f172a] text-[#ffffff] inline-flex items-center justify-center disabled:opacity-30" data-testid="tsf-back-btn"><ArrowLeft size={20} weight="bold" /></button>
          {last || !AUTO.includes(q.kind) ? (
            <button type="button" onClick={ok} disabled={busy} className="h-12 flex-1 rounded-lg bg-[#0f172a] text-[#ffffff] text-[16px] font-semibold hover:bg-[#1e293b] disabled:opacity-60" data-testid={last ? "tsf-submit-btn" : "tsf-ok-btn"}>
              {busy ? "Saving…" : last ? "Submit" : "OK"}
            </button>
          ) : (
            <span className="flex-1 text-center text-[13px] text-[#64748b]" data-testid="tsf-auto-hint">Tap an answer to continue</span>
          )}
        </div>
      </div>
    </div>
  );
}
